import React, { useState, useEffect, useMemo } from "react";
import { Modal, Input, Button, List, Typography, Space, Divider } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Text } = Typography;

const AutoCompleteModal = ({ 
  visible, 
  onCancel, 
  onSave, 
  title, 
  value, 
  suggestions = [], 
  placeholder = "Enter text...",
  onAddSuggestion,
  onRemoveSuggestion,
  fieldKey,
  loading = false
}) => {
  const [currentValue, setCurrentValue] = useState("");
  const [suggestionFilter, setSuggestionFilter] = useState("");
  const [newSuggestion, setNewSuggestion] = useState("");

  // Initialize currentValue when modal opens
  useEffect(() => {
    if (visible) {
      setCurrentValue(value || "");
    }
  }, [visible, value]);

  // Filter suggestions using useMemo to avoid unnecessary recalculations
  const filteredSuggestions = useMemo(() => {
    return suggestions
      .filter(suggestion => 
        suggestion && 
        typeof suggestion === 'string' && 
        suggestion.trim() !== ''
      )
      .filter((suggestion, index, array) => 
        array.indexOf(suggestion) === index // Remove duplicates
      )
      .filter(suggestion =>
        suggestion.toLowerCase().includes(suggestionFilter.toLowerCase())
      );
  }, [suggestions, suggestionFilter]);

  const handleCancel = () => {
    // Reset all state when modal closes
    setCurrentValue("");
    setSuggestionFilter("");
    setNewSuggestion("");
    onCancel();
  };

  const handleSave = () => {
    onSave(currentValue);
    handleCancel(); // This will reset state and close modal
  };

  const handleSuggestionClick = (suggestion) => {
    const newValue = currentValue ? `${currentValue}\n${suggestion}` : suggestion;
    setCurrentValue(newValue);
    // Focus the textarea after a short delay to ensure the DOM has updated
    setTimeout(() => {
      const textarea = document.querySelector('.ant-modal-content textarea');
      if (textarea) {
        textarea.focus();
        // Set cursor position to the end
        textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
      }
    }, 0);
  };

  const handleAddNewSuggestion = async () => {
    if (onAddSuggestion && newSuggestion.trim() && !suggestions.includes(newSuggestion.trim())) {
      await onAddSuggestion(newSuggestion.trim(), fieldKey);
      setNewSuggestion("");
    }
  };

  const handleRemoveSuggestion = async (suggestion) => {
    if (onRemoveSuggestion) {
      await onRemoveSuggestion(suggestion, fieldKey);
    }
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleCancel}
      width={800}
      centered
      destroyOnClose={true}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={loading}>
          Save
        </Button>
      ]}
    >
      <div style={{ display: "flex", gap: "24px", minHeight: "100%" }}>
        {/* Left side - Text editor */}
        <div style={{ flex: 1 }}>
          <TextArea
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            placeholder={placeholder}
            style={{ fontSize: "14px", minHeight: "100%", height: "100%" }}
            autoFocus
          />
        </div>

        {/* Right side - Suggestions management */}
        <div style={{ width: "300px" }}>
          
          {/* Add new suggestion */}
          <Space.Compact style={{ width: "100%", marginBottom: "16px", height: "fit-content" }}>
            <TextArea
              placeholder="New suggestion"
              value={newSuggestion}
              onChange={(e) => setNewSuggestion(e.target.value)}
              style={{ resize: "none" }}
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddNewSuggestion}
              disabled={!newSuggestion.trim()}
              style={{ height: '60px' }}
            ></Button>
          </Space.Compact>

          {/* Filter suggestions */}
          <Input
            placeholder="Filter suggestions"
            value={suggestionFilter}
            onChange={(e) => setSuggestionFilter(e.target.value)}
            style={{ marginBottom: "16px" }}
          />

          {/* Suggestions list */}
          <div style={{ 
            border: "1px solid #f0f0f0", 
            borderRadius: "4px",
            maxHeight: "300px",
            overflow: "auto"
          }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading...
              </div>
            ) : filteredSuggestions.length > 0 ? (
              <List
                size="small"
                dataSource={filteredSuggestions}
                renderItem={(suggestion) => (
                  <List.Item
                    style={{ 
                      padding: "8px 12px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Text 
                      style={{ 
                        cursor: "pointer",
                        flex: 1,
                        marginRight: "8px"
                      }}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </Text>
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => handleRemoveSuggestion(suggestion)}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                {suggestionFilter ? "No matching suggestions" : "No suggestions available"}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AutoCompleteModal; 